import React, { useMemo } from "react";
import classes from "./statusBar.module.scss";
import { RealtimeStatus, useRealtime } from "../contexts/RealtimeContext";
import { COMMAND_PALETTE_HINT, COMMAND_PALETTE_KEYS, openCommandPalette } from "../utils/shortcuts";

const STATUS_COPY: Record<RealtimeStatus, { label: string; hint: string; className: string }> = {
    connecting: { label: "Connecting…", hint: "opening the live connection", className: classes.statusConnecting },
    live: { label: "Live", hint: "updates arrive as they happen", className: classes.statusLive },
    reconnecting: { label: "Reconnecting…", hint: "live updates paused — the page catches up when the connection returns", className: classes.statusReconnecting },
    offline: { label: "Offline", hint: "live updates stopped — refresh the page to reconnect", className: classes.statusOffline },
};

const isLocalHost = (host: string) => host === "localhost" || host === "127.0.0.1" || host.endsWith(".local");

/**
 * A local build pointed at a remote API reads and writes real data — easy to
 * forget, so say it on every page. Null in every other setup.
 */
const getEnvironmentWarning = (): string | null => {
    try {
        const api = new URL(process.env.REACT_APP_API_ENDPOINT || "", window.location.origin);
        if (isLocalHost(window.location.hostname) && !isLocalHost(api.hostname)) {
            return `Local build talking to ${api.hostname}`;
        }
    } catch {
        // unparseable endpoint: nothing to warn about here
    }
    return null;
};

/** Slim footer under the page: live connection, environment warning, search hint, version. */
const StatusBar: React.FC = () => {
    const { realtimeStatus } = useRealtime();
    const envWarning = useMemo(getEnvironmentWarning, []);
    const version = process.env.REACT_APP_VERSION;
    const copy = STATUS_COPY[realtimeStatus];

    return (
        <footer className={classes.statusBar}>
            <span className={`${classes.live} ${copy.className}`} role="status" title={`${copy.label} — ${copy.hint}`}>
                <span className={classes.dot} aria-hidden="true" />
                <span className={classes.liveLabel}>{copy.label}</span>
                <span className={classes.liveHint}>{copy.hint}</span>
            </span>
            {envWarning && (
                <span className={classes.env} title="This browser tab is reading and writing that server's data">
                    <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                    <span className={classes.envLabel}>{envWarning}</span>
                </span>
            )}
            <span className={classes.spacer} />
            <button
                type="button"
                className={`btn-unstyled ${classes.hint}`}
                onClick={openCommandPalette}
                title={`Open the command palette (${COMMAND_PALETTE_HINT})`}
            >
                {COMMAND_PALETTE_KEYS.map((key) => <kbd key={key} className={classes.kbd}>{key}</kbd>)}
                <span className={classes.hintLabel}>search or jump anywhere</span>
            </button>
            {version && <span className={classes.version}>v{version}</span>}
        </footer>
    );
};

export default StatusBar;
